// Version Manager for PWA updates
export class VersionManager {
  private static instance: VersionManager;
  private currentVersion: string = '';
  private buildNumber: number = 0;
  private lastUpdateCheck: number = 0;
  private updateCheckInterval: NodeJS.Timeout | null = null;

  private constructor() {
    this.initializeVersion();
  }

  static getInstance(): VersionManager {
    if (!VersionManager.instance) {
      VersionManager.instance = new VersionManager();
    }
    return VersionManager.instance;
  }

  private initializeVersion() {
    // Wait for version.js to load if it's not already available
    if (!(window as any).APP_VERSION) {
      // Try to load version info from version.json as fallback
      this.loadVersionFromFile();
    } else {
      this.setVersionFromGlobals();
    }
  }

  private setVersionFromGlobals() {
    // Get version from window globals (set by version.js)
    this.currentVersion = (window as any).APP_VERSION || '1.0.0';
    this.buildNumber = parseInt((window as any).APP_BUILD || '0');
    
    console.log('Version Manager initialized from globals:', {
      version: this.currentVersion,
      build: this.buildNumber,
      source: 'version.js'
    });
  }

  private async loadVersionFromFile() {
    try {
      // Try to load version from version.json
      const response = await fetch('/version.json');
      if (response.ok) {
        const data = await response.json();
        this.currentVersion = data.version || '1.0.0';
        this.buildNumber = data.build || 0;
        
        // Set window globals
        (window as any).APP_VERSION = this.currentVersion;
        (window as any).APP_BUILD = this.buildNumber;
        
        console.log('Version Manager initialized from version.json:', {
          version: this.currentVersion,
          build: this.buildNumber,
          source: 'version.json'
        });
      } else {
        // Fallback to environment variables
        this.currentVersion = process.env.REACT_APP_VERSION || '1.0.0';
        this.buildNumber = parseInt(process.env.REACT_APP_BUILD_NUMBER || '0');
        
        (window as any).APP_VERSION = this.currentVersion;
        (window as any).APP_BUILD = this.buildNumber;
        
        console.log('Version Manager initialized from environment:', {
          version: this.currentVersion,
          build: this.buildNumber,
          source: 'environment'
        });
      }
    } catch (error) {
      console.error('Error loading version info:', error);
      // Fallback to environment variables
      this.currentVersion = process.env.REACT_APP_VERSION || '1.0.0';
      this.buildNumber = parseInt(process.env.REACT_APP_BUILD_NUMBER || '0');
      
      (window as any).APP_VERSION = this.currentVersion;
      (window as any).APP_BUILD = this.buildNumber;
      
      console.log('Version Manager initialized with fallback:', {
        version: this.currentVersion,
        build: this.buildNumber,
        source: 'fallback'
      });
    }
  }

  getCurrentVersion(): string {
    return this.currentVersion;
  }

  getCurrentBuild(): number {
    return this.buildNumber;
  }

  getVersionInfo(): {
    version: string;
    build: number;
    timestamp: string;
    environment: string;
  } {
    return {
      version: this.currentVersion,
      build: this.buildNumber,
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV || 'production'
    };
  }

  async checkForUpdates(): Promise<{
    hasUpdate: boolean;
    latestVersion?: string;
    latestBuild?: number;
    updateType?: 'patch' | 'minor' | 'major' | 'build';
  }> {
    try {
      const response = await fetch('/version.json?' + Date.now());
      if (!response.ok) {
        throw new Error('Failed to fetch version info');
      }
      
      const data = await response.json();
      const latestVersion = data.version;
      const latestBuild = data.build || 0;
      
      console.log('Version check:', {
        current: { version: this.currentVersion, build: this.buildNumber },
        latest: { version: latestVersion, build: latestBuild }
      });
      
      // Check if there's a newer version or build
      const hasVersionUpdate = this.isNewerVersion(this.currentVersion, latestVersion);
      const hasBuildUpdate = latestBuild > this.buildNumber;
      
      if (hasVersionUpdate || hasBuildUpdate) {
        const updateType = this.getUpdateType(this.currentVersion, latestVersion, this.buildNumber, latestBuild);
        
        return {
          hasUpdate: true,
          latestVersion,
          latestBuild,
          updateType
        };
      }
      
      return { hasUpdate: false };
    } catch (error) {
      console.error('Error checking for updates:', error);
      return { hasUpdate: false };
    }
  }

  private isNewerVersion(current: string, latest: string): boolean {
    const currentParts = current.split(/[.-]/).map(Number);
    const latestParts = latest.split(/[.-]/).map(Number);
    
    for (let i = 0; i < Math.max(currentParts.length, latestParts.length); i++) {
      const currentPart = currentParts[i] || 0;
      const latestPart = latestParts[i] || 0;
      
      if (latestPart > currentPart) return true;
      if (latestPart < currentPart) return false;
    }
    
    return false;
  }

  private getUpdateType(
    currentVersion: string, 
    latestVersion: string, 
    currentBuild: number, 
    latestBuild: number
  ): 'patch' | 'minor' | 'major' | 'build' {
    // If only build number changed
    if (currentVersion === latestVersion && latestBuild > currentBuild) {
      return 'build';
    }
    
    // If version changed, determine type
    const currentParts = currentVersion.split(/[.-]/).map(Number);
    const latestParts = latestVersion.split(/[.-]/).map(Number);
    
    if (latestParts[0] > currentParts[0]) return 'major';
    if (latestParts[1] > currentParts[1]) return 'minor';
    if (latestParts[2] > currentParts[2]) return 'patch';
    
    return 'build';
  }

  startPeriodicCheck(intervalMs: number = 5 * 60 * 1000): void {
    if (this.updateCheckInterval) {
      clearInterval(this.updateCheckInterval);
    }
    
    this.updateCheckInterval = setInterval(async () => {
      await this.checkForUpdates();
    }, intervalMs);
  }

  stopPeriodicCheck(): void {
    if (this.updateCheckInterval) {
      clearInterval(this.updateCheckInterval);
      this.updateCheckInterval = null;
    }
  }

  // Generate version.json for build process
  generateVersionFile(): string {
    const versionInfo = this.getVersionInfo();
    return JSON.stringify(versionInfo, null, 2);
  }

  // Update version for new builds
  updateVersion(newVersion: string, newBuild: number): void {
    this.currentVersion = newVersion;
    this.buildNumber = newBuild;
    
    (window as any).APP_VERSION = this.currentVersion;
    (window as any).APP_BUILD = this.buildNumber;
    
    console.log('Version updated:', {
      version: this.currentVersion,
      build: this.buildNumber
    });
  }

  // Refresh version information from available sources
  async refreshVersion(): Promise<void> {
    // First try to get from window globals (version.js)
    if ((window as any).APP_VERSION) {
      this.setVersionFromGlobals();
      return;
    }
    
    // If not available, try to load from version.json
    await this.loadVersionFromFile();
  }
}

export const versionManager = VersionManager.getInstance();
